if (!customElements.get('hexagon-spotlight')) {
  class HexagonSpotlight extends HTMLElement {
    constructor() {
      super();

      this.maskedLayers = [];
      this.lightSpot = null;
      this.canvas = null;
      this.ctx = null;
      this.twinkles = [];
      this.hexCentres = [];
      this.gridIndex = new Map();
      this.animationFrame = null;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.isVisible = true;
      this.active = false;
      this.mx = 0;
      this.my = 0;
      this.cx = 0;
      this.cy = 0;
      this.canvasWidth = 0;
      this.canvasHeight = 0;

      this.tileWidth = 50.115;
      this.tileHeight = 29;
      this.twinkleCount = 18;
      this.hexOffsets = [
        [16.744, 0.002],
        [8.374, 14.5],
        [-8.37, 14.498],
        [-16.744, -0.002],
        [-8.374, -14.5],
        [8.371, -14.498],
      ];

      this.pointerMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.motionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handlePointerEnter = this.handlePointerEnter.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerLeave = this.handlePointerLeave.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.animate = this.animate.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.canvas = this.querySelector('.hexagon-spotlight__twinkle');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this.handleResize, { passive: true });
      }

      if (window.IntersectionObserver) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.isVisible = entry ? entry.isIntersecting : true;
        });
        this.intersectionObserver.observe(this);
      }

      this.handleResize();
      this.animationFrame = window.requestAnimationFrame(this.animate);

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.pointerMedia.addEventListener('change', this.handleMediaChange);
      if (this.pointerMedia.matches) this.enableHover();
    }

    disconnectedCallback() {
      this.pointerMedia.removeEventListener('change', this.handleMediaChange);
      this.disableHover();

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else {
        window.removeEventListener('resize', this.handleResize);
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      if (this.animationFrame) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    }

    handleMediaChange() {
      if (this.pointerMedia.matches) {
        this.enableHover();
      } else {
        this.disableHover();
      }
    }

    enableHover() {
      if (this.hoverEnabled) return;
      this.hoverEnabled = true;

      this.maskedLayers = Array.from(
        this.querySelectorAll('.hexagon-spotlight__masked'),
      );
      this.lightSpot = this.querySelector('.hexagon-spotlight__light');

      this.addEventListener('pointerenter', this.handlePointerEnter);
      this.addEventListener('pointermove', this.handlePointerMove);
      this.addEventListener('pointerleave', this.handlePointerLeave);
      this.addEventListener('pointercancel', this.handlePointerLeave);
    }

    disableHover() {
      if (!this.hoverEnabled) return;
      this.hoverEnabled = false;

      this.removeEventListener('pointerenter', this.handlePointerEnter);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      this.removeEventListener('pointercancel', this.handlePointerLeave);

      this.active = false;
      this.classList.remove('is-active');
    }

    handlePointerEnter(event) {
      if (!this.canTrackPointer(event)) return;

      const point = this.getLocalPoint(event);
      this.mx = point.x;
      this.my = point.y;
      this.cx = point.x;
      this.cy = point.y;
      this.updatePositions(point.x, point.y);
      this.active = true;
      this.classList.add('is-active');
    }

    handlePointerMove(event) {
      if (!this.canTrackPointer(event)) return;

      const point = this.getLocalPoint(event);
      this.mx = point.x;
      this.my = point.y;

      if (!this.active) {
        this.cx = point.x;
        this.cy = point.y;
        this.updatePositions(point.x, point.y);
      }

      this.active = true;
      this.classList.add('is-active');
    }

    handlePointerLeave() {
      this.active = false;
      this.classList.remove('is-active');
    }

    handleResize() {
      if (!this.ctx || !this.canvas) return;

      const rect = this.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      this.canvasWidth = width;
      this.canvasHeight = height;
      this.canvas.width = width * dpr;
      this.canvas.height = height * dpr;
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.buildGrid(width, height);
      this.seedTwinkles();
    }

    animate(now) {
      if (this.active) {
        const ease = this.motionMedia.matches ? 1 : 0.15;
        this.cx += (this.mx - this.cx) * ease;
        this.cy += (this.my - this.cy) * ease;
        this.updatePositions(this.cx, this.cy);
      }

      if (this.ctx) {
        if (this.isVisible && !this.motionMedia.matches) {
          this.drawTwinkles(now);
        } else {
          this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        }
      }

      this.animationFrame = window.requestAnimationFrame(this.animate);
    }

    canTrackPointer(event) {
      if (!this.pointerMedia.matches) return false;
      if (!event.pointerType) return true;
      return event.pointerType === 'mouse' || event.pointerType === 'pen';
    }

    getLocalPoint(event) {
      const rect = this.getBoundingClientRect();
      return {
        x: this.clamp(event.clientX - rect.left, 0, rect.width),
        y: this.clamp(event.clientY - rect.top, 0, rect.height),
      };
    }

    updatePositions(x, y) {
      if (this.maskedLayers[0]) {
        const revealPosition = `${x - 240}px ${y - 240}px`;
        this.maskedLayers[0].style.maskPosition = revealPosition;
        this.maskedLayers[0].style.webkitMaskPosition = revealPosition;
      }

      if (this.maskedLayers[1]) {
        const glowPosition = `${x - 120}px ${y - 120}px`;
        this.maskedLayers[1].style.maskPosition = glowPosition;
        this.maskedLayers[1].style.webkitMaskPosition = glowPosition;
      }

      if (this.maskedLayers[2]) {
        const corePosition = `${x - 50}px ${y - 50}px`;
        this.maskedLayers[2].style.maskPosition = corePosition;
        this.maskedLayers[2].style.webkitMaskPosition = corePosition;
      }

      if (this.lightSpot) {
        this.lightSpot.style.transform = `translate(${x - 190}px, ${y - 190}px)`;
      }
    }

    buildGrid(width, height) {
      this.hexCentres = [];
      this.gridIndex = new Map();

      const cols = Math.ceil(width / this.tileWidth) + 2;
      const rows = Math.ceil(height / this.tileHeight) + 2;

      for (let row = -1; row < rows; row += 1) {
        for (let col = -1; col < cols; col += 1) {
          const x1 = col * this.tileWidth + 25.058;
          const y1 = row * this.tileHeight;
          const index1 = this.hexCentres.length;
          this.hexCentres.push({ x: x1, y: y1, col, row, type: 1 });
          this.gridIndex.set(this.gridKey(col, row, 1), index1);

          const x2 = col * this.tileWidth;
          const y2 = row * this.tileHeight + 14.5;
          const index2 = this.hexCentres.length;
          this.hexCentres.push({ x: x2, y: y2, col, row, type: 2 });
          this.gridIndex.set(this.gridKey(col, row, 2), index2);
        }
      }
    }

    seedTwinkles() {
      if (!this.hexCentres.length) return;

      this.twinkles = [];
      const now = performance.now();

      for (let index = 0; index < this.twinkleCount; index += 1) {
        const twinkle = this.spawnTwinkle(now);
        twinkle.start = now - Math.random() * twinkle.duration;
        this.twinkles.push(twinkle);
      }
    }

    spawnTwinkle(now) {
      const index = Math.floor(Math.random() * this.hexCentres.length);
      const hex = this.hexCentres[index];
      return {
        idx: index,
        x: hex.x,
        y: hex.y,
        col: hex.col,
        row: hex.row,
        type: hex.type,
        start: now,
        duration: 2000 + Math.random() * 3000,
        maxAlpha: 0.06 + Math.random() * 0.09,
      };
    }

    drawTwinkles(now) {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      for (let index = 0; index < this.twinkles.length; index += 1) {
        const twinkle = this.twinkles[index];
        const elapsed = now - twinkle.start;

        if (elapsed < 0) continue;

        if (elapsed > twinkle.duration) {
          this.twinkles[index] = this.spawnTwinkle(now);
          continue;
        }

        const progress = elapsed / twinkle.duration;
        const alpha = twinkle.maxAlpha * Math.sin(progress * Math.PI);

        if (alpha <= 0.005) continue;

        this.drawHex(twinkle.x, twinkle.y, alpha);

        const neighbourAlpha = alpha * 0.35;
        if (neighbourAlpha <= 0.005) continue;

        const neighbours = this.getNeighbours(twinkle.col, twinkle.row, twinkle.type);
        neighbours.forEach((neighbourIndex) => {
          const neighbour = this.hexCentres[neighbourIndex];
          if (neighbour) this.drawHex(neighbour.x, neighbour.y, neighbourAlpha);
        });
      }
    }

    drawHex(centreX, centreY, alpha) {
      this.ctx.beginPath();

      this.hexOffsets.forEach(([offsetX, offsetY], index) => {
        const x = centreX + offsetX;
        const y = centreY + offsetY;

        if (index === 0) this.ctx.moveTo(x, y);
        else this.ctx.lineTo(x, y);
      });

      this.ctx.closePath();
      this.ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
      this.ctx.lineWidth = 0.5;
      this.ctx.stroke();
    }

    getNeighbours(col, row, type) {
      const keys =
        type === 1
          ? [
              this.gridKey(col, row - 1, 2),
              this.gridKey(col, row, 2),
              this.gridKey(col + 1, row - 1, 2),
              this.gridKey(col + 1, row, 2),
              this.gridKey(col - 1, row, 1),
              this.gridKey(col + 1, row, 1),
            ]
          : [
              this.gridKey(col - 1, row, 1),
              this.gridKey(col, row, 1),
              this.gridKey(col - 1, row + 1, 1),
              this.gridKey(col, row + 1, 1),
              this.gridKey(col, row - 1, 2),
              this.gridKey(col, row + 1, 2),
            ];

      return keys
        .map((key) => this.gridIndex.get(key))
        .filter((value) => value !== undefined);
    }

    gridKey(col, row, type) {
      return `${col},${row},${type}`;
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  customElements.define('hexagon-spotlight', HexagonSpotlight);
}
